import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ModalV2, QueryLoader } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { useGetPaymentAccountQuery } from "~/graphql/supergraphHooks";
import { type PaymentAccountInput } from "~/graphql/supergraphTypes";
import { AccountForm } from "../AccountForm";
import { getPaymentInstructionSchema } from "../AccountForm/schema";
import { usePaymentInstructions } from "../PaymentInstructionsContext";
import { PaymentInstructionFormValues } from "../types";
import { getAccountInitialValues, getProfilesMap } from "../utils";
import { prepareUpdateRequestPayload } from "./utils";

export type Props = {
  title: string;
  handleSubmit: (values: PaymentAccountInput) => void;
  handleNavigateBack: () => void;
};

export const BeneficiaryAccountModal = ({ title, handleSubmit, handleNavigateBack }: Props) => {
  const { beneficiaryId } = useParams<{ beneficiaryId: string }>();
  const { programId, paymentPrefId, investor, profiles } = usePaymentInstructions();

  const beneficiaryResult = useGetPaymentAccountQuery({
    variables: { id: parseInt(beneficiaryId), programId: programId },
    fetchPolicy: "network-only",
    skip: !beneficiaryId,
  });

  const initialValues = useMemo(() => {
    return getAccountInitialValues(beneficiaryResult?.data?.compliancePaymentAccount);
  }, [beneficiaryResult?.data?.compliancePaymentAccount]);

  const handleFormSubmit = (values: PaymentInstructionFormValues) => {
    const requestBody = prepareUpdateRequestPayload(
      values,
      initialValues,
      beneficiaryResult?.data?.compliancePaymentAccount || undefined,
    );
    handleSubmit(requestBody);
  };

  return (
    <ModalV2
      title={title}
      show={true}
      onHide={handleNavigateBack}
      backdrop={true}
      closeOnClickOutside={false}
    >
      <QueryLoader result={beneficiaryResult}>
        <Form<PaymentInstructionFormValues>
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
          yupValidationSchema={getPaymentInstructionSchema(!paymentPrefId)}
          submitOnlyChangedValues={false}
        >
          <ModalV2.Body>
            <AccountForm
              profilesMap={getProfilesMap(investor, profiles)}
              programId={programId}
              showBankAndCountry={!paymentPrefId}
              onClose={handleNavigateBack}
            />
          </ModalV2.Body>
        </Form>
      </QueryLoader>
    </ModalV2>
  );
};
